import React, {memo, SyntheticEvent, useCallback, useEffect, useMemo, useState} from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Euro as EuroIcon,
  NoPhotography as NoPhotographyIcon,
  Percent as PercentIcon,
  PhotoCamera as PhotoCameraIcon,
} from "@mui/icons-material";
import {QrScanner} from '@yudiel/react-qr-scanner';
import {DateRangePicker} from 'rsuite';
import {endOfMonth, endOfWeek, startOfMonth, startOfWeek} from "rsuite/cjs/internals/utils/date";
import {RangeType} from "rsuite/DateRangePicker";

import {importAll} from "../../../Utils";
import {useDebounce, useSupabase} from "../../../Hooks";
import {useAppStore} from "../../../Stores";
import {
  FormLabelDiscountDetailEnum,
  FormLabelMechanicalEnum,
  FormLabelMechanicalGroupEnum,
  FormLabelType,
} from "../Types";
import {
  getAvailableBanners,
  getAvailableTitles,
  getDefaultTitleValue,
  getFormLabelMechanicalEnumFromGroup,
  hasDiscountField,
  hasDiscountPrecision,
  hasOldPriceField,
  hasScanField,
  isDiscountEuro,
  isDiscountPercent, isOldPriceEuro, isOldPricePercent,
} from "../Config";
import HaloContainer from "./HaloContainer";

interface LabelFormProps {
  id: number;
  isFirst: boolean;
}

export const Form: React.FC<LabelFormProps> = memo(({
  id,
  isFirst
}) => {
  const printOptionsSize = useAppStore((state) => state.printOptions?.size);

  if (!printOptionsSize) {
    throw new Error("Impossible de trouver les options d'impression.");
  }

  const data = useAppStore((state) => state.labels.find((label) => label.id === id) || null);

  if (!data) {
    throw new Error("Impossible de trouver les données de l'étiquette.");
  }

  const addLabel = useAppStore((state) => state.addLabel);
  const updateLabel = useAppStore((state) => state.updateLabel);
  const deleteLabel = useAppStore((state) => state.deleteLabel);

  const scanRef = React.useRef<any>(null);
  const [openScan, setOpenScan] = useState<boolean>(false)
  const [label, setLabel] = useState<FormLabelType>(data as FormLabelType);
  const debouncedValues = useDebounce(label, 500);
  const [productDb, productDbLoading, productDbError] = useSupabase(debouncedValues.ean);

  // Barcode scanner
  const onClickPhoneCamera = useCallback(() => {
    setOpenScan(!openScan);
  }, [openScan]);

  const onScanUpdate = useCallback(
    (result: any) => {
    if (result) {
      setLabel({...label, ean: result});
      setOpenScan(false);
    }
  }, [label]);

  useEffect(() => {
    if (openScan) {
      scanRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [openScan]);

  // Form handles
  const handleChange = useCallback(
    (fieldKey: string, inputValue: any) => {
      let propertiesOverwrited: Partial<FormLabelType> = {};

      if (fieldKey === 'mechanicalGroup') {
        propertiesOverwrited.mechanical = getFormLabelMechanicalEnumFromGroup(inputValue)[0];
        propertiesOverwrited.title = getDefaultTitleValue(propertiesOverwrited.mechanical);
        propertiesOverwrited.banner = getAvailableBanners(propertiesOverwrited.mechanical)[0];
      }

      if (fieldKey === 'mechanical') {
        propertiesOverwrited.title = getDefaultTitleValue(inputValue);
        propertiesOverwrited.banner = getAvailableBanners(inputValue)[0];
      }


      setLabel({
        ...label,
        ...propertiesOverwrited,
        [fieldKey]: inputValue,
      });
    },
    [label]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLabel({
        ...label,
        [event.target.name]: event.target.value,
      });
    },
    [label]
  );

  const handleSelectChange = useCallback(
    (event: SelectChangeEvent) => {
      setLabel({
        ...label,
        [event.target.name]: event.target.value,
      });
    },
    [label]
  );

  // EAN search
  useEffect(() => {
    if(productDb) {
      setLabel({
        ...label,
        name: productDb.name ?? '',
        capacity: `${productDb.quantity && productDb.quantity > 0 ? productDb.quantity : ''}${productDb.unit ?? ''}`,
      })
    }
  }, [productDb]);

  // Callback to parent
  useEffect(() => {
    if (label.mechanical === FormLabelMechanicalEnum.SPECIAL_ROUND) {
      debouncedValues.oldPrice = String(parseInt(debouncedValues.oldPrice));
    }

    updateLabel(debouncedValues.id, debouncedValues);
  }, [debouncedValues]);

  // Utils - Icons
  const icons = importAll(require.context('../../../Icons', false, /\.(svg)$/));
  const topIconsOrder = useMemo(() => ['Euro', 'Lot-de-deux', 'Pouce', 'Pourcentage', 'Top'], []);
  const iconsTop = icons
    .filter((icon: any) => topIconsOrder.includes(icon.split('/').pop().split('.').shift()))
    .sort((a: any, b: any) => {
      return topIconsOrder.indexOf(a.split('/').pop().split('.').shift()) - topIconsOrder.indexOf(b.split('/').pop().split('.').shift());
    });
  iconsTop.forEach((icon: any) => {
    icons.splice(icons.indexOf(icon), 1);
  });

  // Utils - DateRangePicker
  // Get next monday
  const nextMonday = new Date();
  nextMonday.setDate(nextMonday.getDate() + (1 + 7 - nextMonday.getDay()) % 7);
  const predefinedRanges: RangeType[] = [
    {
      label: 'Semaine prochaine',
      value: [startOfWeek(nextMonday, {weekStartsOn: 1}), endOfWeek(nextMonday, {weekStartsOn: 1})],
      placement: 'left',
    },
    {
      label: 'Ce mois-ci',
      value: [startOfMonth(new Date()), endOfMonth(new Date())],
      placement: 'left'
    },
    {
      label: 'Jusqu\'à la fin du mois',
      value: [new Date(), endOfMonth(new Date())],
      placement: 'left'
    },
    {
      label: 'Jusqu\'à m+1',
      value: [new Date(), endOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() + 2, 0))],
      placement: 'left'
    },
    {
      label: 'Jusqu\'à m+2',
      value: [new Date(), endOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() + 3, 0))],
      placement: 'left'
    },
    {
      label: 'Effacer',
      value: null,
      placement: 'left'
    },
  ];

  return (
    <>
      <Box component="form" sx={{mt: 1}}>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{textAlign: 'center'}}>
            <ToggleButtonGroup
              color="primary"
              value={label.mechanicalGroup}
              exclusive
              onChange={(event: React.MouseEvent<HTMLElement>, value: any) => {
                handleChange('mechanicalGroup', value);
              }}
              aria-label="Platform"
            >
              {Object.entries(FormLabelMechanicalGroupEnum).map(
                ([key, value]) => {
                  return (
                    <ToggleButton value={value} key={key}>
                      {value}
                    </ToggleButton>
                  )
                }
              )}
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              renderInput={(params) => <TextField
                {...params}
                fullWidth
                size="small"
                margin="normal"
                id="mechanical"
                label="Mécanique"
                name="mechanical"
              />}
              disableClearable
              options={getFormLabelMechanicalEnumFromGroup(label.mechanicalGroup).map(
                (mechanical) => ({
                  key: mechanical,
                  label: mechanical,
                })
              )}
              onChange={(event: SyntheticEvent<Element, Event>, inputValue: any) => {
                handleChange('mechanical', inputValue.key);
              }}
              value={label.mechanical}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              renderInput={(params) => <TextField
                {...params}
                fullWidth
                size="small"
                margin="normal"
                id="title"
                label="Titre"
                name="title"
              />}
              options={getAvailableTitles(label.mechanical)}
              onInputChange={(event: SyntheticEvent<Element, Event>, inputValue: any) => {
                handleChange('title', inputValue);
              }}
              inputValue={label.title}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              renderInput={(params) => <TextField
                {...params}
                fullWidth
                size="small"
                margin="normal"
                id="banner"
                label="Bandeau répétitif"
                name="banner"
              />}
              options={getAvailableBanners(label.mechanical)}
              onInputChange={(event: SyntheticEvent<Element, Event>, inputValue: any) => {
                handleChange('banner', inputValue);
              }}
              inputValue={label.banner}
            />
          </Grid>
        </Grid>

        {hasScanField(label.mechanical) && (
          <Grid container spacing={2}>
            {openScan && (
              <Grid item xs={12}>
                <div ref={scanRef}>
                  <QrScanner onDecode={onScanUpdate} />
                </div>
              </Grid>
            )}
            <Grid item xs={11}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                id="ean"
                {...(!!productDb || !!productDbError) && {focused: true}}
                label="Code EAN"
                name="ean"
                value={label.ean}
                onChange={handleInputChange}
                color={productDb ? 'success' : productDbError ? 'error' : 'primary'}
                error={!!productDbError}
                helperText={productDbError ? productDbError : productDbLoading ? 'Recherche en cours...' : productDb ? 'Produit trouvé et importé' : ''}
              />
            </Grid>

            <Grid item xs={1}>
              <IconButton aria-label="Scanner avec la caméra" sx={{mt: 2}} onClick={onClickPhoneCamera}>
                {openScan ? <NoPhotographyIcon color="error" /> : <PhotoCameraIcon />}
              </IconButton>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{mt: 2}}>
              <InputLabel id="icon-label" size="small">
                Icon
              </InputLabel>
              <Select
                size="small"
                labelId="icon-label"
                id="icon"
                name="icon"
                value={label.icon}
                label="Icon"
                onChange={handleSelectChange}
              >
                <MenuItem value="none" key="none">
                  Aucune
                </MenuItem>

                <MenuItem disabled><div style={{borderBottom: "1px solid black", width: "100%", margin: "10px 0"}}></div></MenuItem>

                {iconsTop && iconsTop.map(function (image: any, index: number) {
                  const fileName = image.split('/').pop().split('.').shift();
                  const componentName = fileName.charAt(0).toUpperCase() + fileName.slice(1);

                  return (
                    <MenuItem value={componentName} key={index}>
                      <div className='iconsItemPreview'>
                        <img alt={fileName} src={image}/>
                      </div>
                      {fileName.replace(/[^a-zA-Z]/g, ' ')}
                    </MenuItem>
                  )
                })}

                {iconsTop.length > 0 && <MenuItem disabled><div style={{borderBottom: "1px solid black", width: "100%", margin: "10px 0"}}></div></MenuItem>}

                {icons.map(function (image: any, index: number) {
                  const fileName = image.split('/').pop().split('.').shift();
                  const componentName = fileName.charAt(0).toUpperCase() + fileName.slice(1);

                  return (
                    <MenuItem value={componentName} key={index}>
                      <div className='iconsItemPreview'>
                        <img alt={fileName} src={image}/>
                      </div>
                      {fileName.replace(/[^a-zA-Z]/g, ' ')}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                id="name"
                label="Détail de la promotion"
                name="name"
                value={label.name}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                id="capacity"
                label="Format de la promotion"
                name="capacity"
                value={label.capacity}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </Grid>

        <HaloContainer options={{childList: true, subtree: false}}>
          {(hasOldPriceField(label.mechanical) || hasDiscountField(label.mechanical)) && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <HaloContainer>
                  {hasOldPriceField(label.mechanical) && (
                    <TextField
                      fullWidth
                      size="small"
                      margin="normal"
                      id="oldPrice"
                      label="Prix unitaire"
                      name="oldPrice"
                      type="number"
                      value={label.oldPrice}
                      onChange={handleInputChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {isOldPriceEuro(label.mechanical) && <EuroIcon fontSize="small"/>}
                            {isOldPricePercent(label.mechanical) && <PercentIcon fontSize="small"/>}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </HaloContainer>
              </Grid>

              <Grid item xs={12} sm={6}>
                <HaloContainer>
                  {hasDiscountField(label.mechanical) && (
                    <TextField
                      fullWidth
                      size="small"
                      margin="normal"
                      id="discount"
                      label="Remise de"
                      name="discount"
                      type="number"
                      value={label.discount}
                      onChange={handleInputChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {isDiscountEuro(label.mechanical) && <EuroIcon fontSize="small"/>}
                            {isDiscountPercent(label.mechanical) && <PercentIcon fontSize="small"/>}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </HaloContainer>
              </Grid>
            </Grid>
          )}
        </HaloContainer>

        {hasDiscountPrecision(label.mechanical) && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                id="discountPrecision"
                label="Précision"
                name="discountPrecision"
                value={label.discountPrecision}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Autocomplete
              freeSolo
              renderInput={(params) => <TextField
                {...params}
                fullWidth
                size="small"
                margin="normal"
                id="discountDetail"
                label="Détail promotion"
                name="discountDetail"
              />}
              options={
                Object.keys(FormLabelDiscountDetailEnum).map((key) => ({
                  key,
                  label: FormLabelDiscountDetailEnum[key as keyof typeof FormLabelDiscountDetailEnum],
                }))
              }
              onInputChange={(event: SyntheticEvent<Element, Event>, inputValue: any) => {
                handleChange('discountDetail', inputValue);
              }}
              inputValue={label.discountDetail}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ paddingTop: { sm: 2 } }}>
              <DateRangePicker
                block
                ranges={predefinedRanges}
                weekStart={1}
                size="lg"
                value={label.discountLimit}
                onChange={(value) => {
                  handleChange('discountLimit', value)
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            {isFirst ? null : (
              <Button
                fullWidth
                variant="contained"
                sx={{mt: 3, mb: 2}}
                onClick={() => deleteLabel(label.id)}
                color={"error"}
              >
                Supprimer l'étiquette
              </Button>
            )}
          </Grid>

          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              sx={{mt: 3, mb: 2}}
              onClick={() => addLabel({})}
              color={"primary"}
            >
              Ajouter une étiquette
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
});

export default Form;
